// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 52-greenlight-install.md

import * as React from 'react';
import { Info } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';

/** How often an open, untouched drafted step re-reads the install context; matches the DNS readiness re-check. */
export const DRAFT_REREAD_MS = 10_000;

export interface InstallDraftState<T> {
  /** The draft the screen shows: the latest one until the operator edits, then the one they saw. */
  draft: T | undefined;
  touched: boolean;
  /** Call from every input handler; a draft that arrives afterwards never replaces what was typed. */
  markTouched: () => void;
}

export function useInstallDraft<T>(
  incoming: T | undefined,
  onTouched?: () => void,
): InstallDraftState<T> {
  const [touched, setTouched] = React.useState(false);
  const [draft, setDraft] = React.useState<T | undefined>(incoming);
  const incomingKey = JSON.stringify(incoming ?? null);

  React.useEffect(() => {
    if (!touched && incoming !== undefined) setDraft(incoming);
    // Keyed on content: each context re-read hands back a new object.
  }, [incomingKey, touched]);

  const touchedRef = React.useRef(false);
  const markTouched = React.useCallback(() => {
    if (touchedRef.current) return;
    touchedRef.current = true;
    setTouched(true);
    onTouched?.();
  }, [onTouched]);

  return { draft, touched, markTouched };
}

/** Runs `apply` whenever the shown draft changes, for forms that seed their state only once. */
export function useApplyDraft<T>(draft: T | undefined, apply: (draft: T) => void): void {
  const key = JSON.stringify(draft ?? null);
  const applyRef = React.useRef(apply);
  applyRef.current = apply;
  React.useEffect(() => {
    if (draft !== undefined) applyRef.current(draft);
  }, [key]);
}

/**
 * The install API is anonymous during the wizard's window, so the note says
 * only when the values arrived, never who sent them.
 */
export function DraftNote(): React.JSX.Element {
  return (
    <Alert data-testid="install-draft-note">
      <Info className="h-4 w-4" />
      <AlertDescription>
        These values were filled in before the wizard opened. Check them before you continue.
      </AlertDescription>
    </Alert>
  );
}
